<template>
  <div class="kitchen-page">
    <div class="kitchen-top">
      <div class="kitchen-header">
        <h1 class="kitchen-title">Kitchen Stock</h1>
        <div class="kitchen-actions">
          <button class="btn btn-primary" type="button" @click="openAddItemModal">
            ➕ Add Item
          </button>
          <button class="btn btn-secondary" type="button" @click="openLogs">
            📜 View Logs
          </button>
        </div>
      </div>

      <div v-if="viewMode === 'dashboard'" class="filter-tabs" role="tablist" aria-label="Stock filters">
        <button
          class="tab"
          :class="{ active: filterTab === 'ALL' }"
          type="button"
          role="tab"
          aria-selected="filterTab === 'ALL'"
          @click="filterTab = 'ALL'"
        >
          ALL
        </button>
        <button
          class="tab"
          :class="{ active: filterTab === 'LOW' }"
          type="button"
          role="tab"
          aria-selected="filterTab === 'LOW'"
          @click="filterTab = 'LOW'"
        >
          LOW
        </button>
        <button
          class="tab"
          :class="{ active: filterTab === 'OUT' }"
          type="button"
          role="tab"
          aria-selected="filterTab === 'OUT'"
          @click="filterTab = 'OUT'"
        >
          OUT
        </button>
      </div>
    </div>

    <div class="kitchen-body">
      <!-- Stock Dashboard -->
      <section v-if="viewMode === 'dashboard'" class="stock-section" aria-label="Kitchen stock list">
        <div v-if="itemsLoading" class="skeleton-list" aria-busy="true">
          <div v-for="n in 8" :key="`sk-${n}`" class="skeleton-card">
            <div class="skeleton-title" />
            <div class="skeleton-row" />
            <div class="skeleton-row" />
          </div>
        </div>

        <div v-else>
          <div v-if="items.length === 0" class="empty-state">
            <div class="empty-title">No kitchen items yet</div>
            <button class="btn btn-primary" type="button" @click="openAddItemModal">Add Item</button>
          </div>

          <div v-else class="items-list">
            <div
              v-for="item in visibleItems"
              :key="item.itemid"
              class="item-card"
              :class="itemCardClass(item)"
            >
              <div class="item-header">
                <div class="item-name-wrap">
                  <div class="item-name" :class="{ 'item-name-out': isOut(item) }">
                    {{ item.name }}
                  </div>
                  <div v-if="isOut(item)" class="out-label">Out of Stock</div>
                </div>

                <span class="status-badge" :class="statusBadgeClass(item)">
                  {{ computedStatus(item) }}
                </span>
              </div>

              <div class="item-quantity-row">
                <div class="item-quantity">
                  {{ formatNumber(item.current_quantity) }} <span class="unit">{{ item.unit }}</span>
                </div>
                <div class="item-item-loader" v-if="isItemUpdating(item.itemid)">
                  <div class="tiny-spinner" aria-hidden="true" />
                  <span class="loader-text">Updating</span>
                </div>
              </div>

              <div class="quick-update-row">
                <div class="quick-group quick-group-consume" role="group" aria-label="Consume shortcuts">
                  <div class="quick-group-title">Consume</div>
                  <div class="quick-group-buttons">
                    <button
                      class="quick-btn quick-consume"
                      type="button"
                      :disabled="isItemUpdating(item.itemid) || !canConsume(item, 1)"
                      @click="quickUpdate(item, 'CONSUME', 1)"
                      aria-label="Consume 1"
                      title="Consume 1"
                    >
                      −1
                    </button>
                    <button
                      class="quick-btn quick-consume"
                      type="button"
                      :disabled="isItemUpdating(item.itemid) || !canConsume(item, 5)"
                      @click="quickUpdate(item, 'CONSUME', 5)"
                      aria-label="Consume 5"
                      title="Consume 5"
                    >
                      −5
                    </button>
                  </div>
                </div>

                <div class="quick-group quick-group-add" role="group" aria-label="Add shortcuts">
                  <div class="quick-group-title">Add</div>
                  <div class="quick-group-buttons">
                    <button
                      class="quick-btn quick-add"
                      type="button"
                      :disabled="isItemUpdating(item.itemid)"
                      @click="quickUpdate(item, 'ADD', 5)"
                      aria-label="Add 5"
                      title="Add 5"
                    >
                      +5
                    </button>
                    <button
                      class="quick-btn quick-add"
                      type="button"
                      :disabled="isItemUpdating(item.itemid)"
                      @click="quickUpdate(item, 'ADD', 10)"
                      aria-label="Add 10"
                      title="Add 10"
                    >
                      +10
                    </button>
                  </div>
                </div>
              </div>

              <div class="item-actions-row">
                <button
                  class="btn btn-ghost"
                  type="button"
                  :disabled="isItemUpdating(item.itemid)"
                  @click="openUpdateStockModal(item)"
                >
                  Update
                </button>
                <button
                  class="btn btn-ghost"
                  type="button"
                  :disabled="isItemUpdating(item.itemid)"
                  @click="openThresholdModal(item)"
                >
                  Set Low Stock Threshold
                </button>
                <button
                  class="btn btn-danger-ghost"
                  type="button"
                  :disabled="isItemUpdating(item.itemid) || deletingItemId === item.itemid"
                  @click="askDeleteItem(item)"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Stock Logs -->
      <section v-else class="logs-section" aria-label="Kitchen stock logs">
        <div class="logs-header">
          <button class="btn btn-ghost" type="button" @click="backToStock">← Back to Stock</button>
          <h2 class="logs-title">Stock Logs</h2>
        </div>

        <div class="logs-filters">
          <label class="filter-label" for="logsItemFilter">Item</label>
          <select
            id="logsItemFilter"
            class="select"
            v-model="logsFilterItemId"
            :disabled="logsLoading || itemsLoading"
          >
            <option value="ALL">All items</option>
            <option
              v-for="it in items"
              :key="it.itemid"
              :value="it.itemid"
            >
              {{ it.name }}
            </option>
          </select>
        </div>

        <div v-if="!logsLoading" class="logs-columns" aria-hidden="true">
          <div class="logs-col-change">Added / Consumed</div>
          <div class="logs-col-details">
            <div class="logs-details-heading-row">
              <span class="logs-heading-item">Item</span>
              <span class="logs-heading-meta-line">
                <!-- Reserve space so the "Date" heading aligns with the row date text (which comes after "by ..."). -->
                <span class="logs-heading-by logs-heading-by--placeholder">by Warden</span>
                <span class="logs-heading-date">Date</span>
              </span>
            </div>
            <div class="logs-heading-note">Note</div>
          </div>
        </div>

        <div v-if="logsLoading" class="skeleton-list" aria-busy="true">
          <div v-for="n in 8" :key="`sk-log-${n}`" class="skeleton-card skeleton-card--log">
            <div class="skeleton-row" />
            <div class="skeleton-row skeleton-row--w" />
          </div>
        </div>

        <div v-else>
          <div v-if="filteredLogs.length === 0" class="empty-state">
            <div class="empty-title">No stock logs found</div>
          </div>

          <div v-else class="logs-list">
            <div
              v-for="log in filteredLogs"
              :key="log.stockid || `${log.itemid}-${log.createdat}-${log.type}`"
              class="log-row"
              :class="log.type === 'CONSUME' ? 'log-row--consume' : 'log-row--add'"
            >
              <div class="log-amount-block">
                <div class="log-amount-value">
                  <span class="log-sign">{{ log.type === 'CONSUME' ? '-' : '+' }}</span>
                  {{ formatNumber(log.quantity) }}
                  <span class="log-unit">{{ itemUnit(log.itemid) }}</span>
                </div>
                <div class="log-type">
                  {{ log.type === 'CONSUME' ? 'Consumed' : 'Added' }}
                </div>
              </div>

              <div class="log-details">
                <div class="log-main-line">
                  <div class="log-item-name">{{ itemName(log.itemid) }}</div>
                  <div class="log-meta-line">
                    <span class="log-by">by {{ log.type === 'CONSUME' ? 'Warden' : 'Admin' }}</span>
                    <span class="log-date">{{ formatShortDate(log.createdat) }}</span>
                  </div>
                </div>
                <div v-if="log.note" class="log-note">{{ log.note }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Toasts -->
    <div class="toast-container">
      <transition-group name="toast" tag="div">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast"
          :class="[`toast-${toast.type}`]"
        >
          <div class="toast-icon" aria-hidden="true">
            <svg v-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <svg v-else-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </div>
          <div class="toast-content">
            {{ toast.message }}
          </div>
          <button class="toast-close" type="button" @click="removeToast(toast.id)" aria-label="Close toast">
            ✕
          </button>
        </div>
      </transition-group>
    </div>

    <!-- Add Item Modal -->
    <div
      v-if="showAddItemModal"
      class="modal-overlay"
      @click.self="closeAddItemModal"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-content kitchen-modal">
        <div class="modal-header">
          <h2 class="modal-title">Add Kitchen Item</h2>
          <button class="close-btn" type="button" @click="closeAddItemModal" aria-label="Close modal">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <div class="form-group">
              <label class="label">Item Name</label>
              <input
                ref="addItemNameInput"
                v-model.trim="addForm.name"
                class="input"
                :class="{ 'input-error': !!addErrors.name }"
                placeholder="e.g., Rice"
                @keydown.enter.prevent="submitAddItem"
              />
              <div v-if="addErrors.name" class="field-error">{{ addErrors.name }}</div>
            </div>

            <div class="form-group">
              <label class="label">Unit</label>
              <select v-model="addForm.unit" class="input">
                <option value="kg">kg</option>
                <option value="pcs">pcs</option>
                <option value="litre">litre</option>
              </select>
            </div>

            <div class="form-group">
              <label class="label">Current Quantity</label>
              <input
                v-model.number="addForm.current_quantity"
                type="number"
                class="input"
                :class="{ 'input-error': !!addErrors.current_quantity }"
                placeholder="e.g., 10"
                min="0"
                step="0.25"
                @keydown.enter.prevent="submitAddItem"
              />
              <div v-if="addErrors.current_quantity" class="field-error">{{ addErrors.current_quantity }}</div>
            </div>

            <div class="form-group">
              <label class="label">Set Low Stock Threshold</label>
              <input
                v-model.number="addForm.low_stock_threshold"
                type="number"
                class="input"
                :class="{ 'input-error': !!addErrors.low_stock_threshold }"
                placeholder="e.g., 25"
                min="0"
                step="0.25"
                @keydown.enter.prevent="submitAddItem"
              />
              <div v-if="addErrors.low_stock_threshold" class="field-error">{{ addErrors.low_stock_threshold }}</div>
            </div>
          </div>
        </div>

        <div class="modal-footer kitchen-modal-footer">
          <button class="save-button" type="button" :disabled="addSubmitLoading" @click="submitAddItem">
            {{ addSubmitLoading ? 'Adding...' : 'Add Item' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Update Stock Modal -->
    <div
      v-if="showUpdateStockModal"
      class="modal-overlay"
      @click.self="closeUpdateStockModal"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-content kitchen-modal">
        <div class="modal-header">
          <h2 class="modal-title">Update Stock</h2>
          <button class="close-btn" type="button" @click="closeUpdateStockModal" aria-label="Close modal">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <div class="form-group">
              <label class="label">Item</label>
              <input class="input" :value="updateModalItem?.name || ''" disabled />
            </div>

            <div class="form-group">
              <label class="label">Type</label>
              <div class="radio-row" role="radiogroup" aria-label="Stock update type">
                <label class="radio">
                  <input type="radio" value="ADD" v-model="updateForm.type" />
                  <span>Add</span>
                </label>
                <label class="radio">
                  <input type="radio" value="CONSUME" v-model="updateForm.type" />
                  <span>Consume</span>
                </label>
              </div>
            </div>

            <div class="form-group">
              <label class="label">Quantity</label>
              <input
                ref="updateQuantityInput"
                v-model.number="updateForm.quantity"
                class="input input-large"
                type="number"
                min="0"
                step="0.25"
                placeholder="Enter quantity"
                :class="{ 'input-error': !!updateErrors.quantity }"
                @keydown.enter.prevent="submitUpdateStock"
              />
              <div v-if="updateErrors.quantity" class="field-error">{{ updateErrors.quantity }}</div>
            </div>

            <div class="form-group">
              <label class="label">Note</label>
              <textarea
                v-model="updateForm.note"
                class="input textarea"
                placeholder="Optional note"
                rows="3"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer kitchen-modal-footer">
          <button class="save-button" type="button" :disabled="updateSubmitLoading" @click="submitUpdateStock">
            {{ updateSubmitLoading ? 'Updating...' : 'Update' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Threshold Modal -->
    <div
      v-if="showThresholdModal"
      class="modal-overlay"
      @click.self="closeThresholdModal"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-content kitchen-modal">
        <div class="modal-header">
          <h2 class="modal-title">Set Low Stock Threshold</h2>
          <button class="close-btn" type="button" @click="closeThresholdModal" aria-label="Close modal">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <div class="form-group">
              <label class="label">Item</label>
              <input class="input" :value="thresholdModalItem?.name || ''" disabled />
            </div>

            <div class="form-group">
              <label class="label">Low Stock Threshold</label>
              <input
                v-model.number="thresholdForm.low_stock_threshold"
                type="number"
                class="input input-large"
                min="0"
                step="0.25"
                :class="{ 'input-error': !!thresholdErrors.low_stock_threshold }"
                @keydown.enter.prevent="submitThresholdUpdate"
              />
              <div v-if="thresholdErrors.low_stock_threshold" class="field-error">
                {{ thresholdErrors.low_stock_threshold }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer kitchen-modal-footer">
          <button class="save-button" type="button" :disabled="thresholdSubmitLoading" @click="submitThresholdUpdate">
            {{ thresholdSubmitLoading ? 'Saving...' : 'Save Threshold' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirm"
      class="modal-overlay"
      @click.self="closeDeleteConfirm"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-content delete-modal">
        <div class="modal-header delete-header">
          <div class="modal-icon delete-icon" aria-hidden="true">🗑️</div>
          <h3 class="modal-title">Delete Item</h3>
          <p class="modal-description">
            Are you sure you want to delete <b>{{ deleteModalItem?.name }}</b>?
          </p>
        </div>
        <div class="modal-actions">
          <button class="btn btn-secondary" type="button" @click="closeDeleteConfirm" :disabled="deletingItemId">
            Cancel
          </button>
          <button class="btn btn-danger" type="button" @click="confirmDelete" :disabled="deletingItemId">
            {{ deletingItemId ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';

export default {
  name: 'KitchenStockDashboard',
  props: {
    objectData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      viewMode: 'dashboard', // 'dashboard' | 'logs'
      filterTab: 'ALL', // 'ALL' | 'LOW' | 'OUT'

      itemsLoading: false,
      logsLoading: false,
      items: [],
      logs: [],

      logsFilterItemId: 'ALL',

      // Quick update per-item loader (and optimistic updates)
      itemUpdatingById: {},
      prevQtyById: {},

      // Delete UI
      showDeleteConfirm: false,
      deleteModalItem: null,
      deletingItemId: null,

      // Add item modal
      showAddItemModal: false,
      addSubmitLoading: false,
      addForm: {
        name: '',
        unit: 'kg',
        current_quantity: 0,
        low_stock_threshold: null
      },
      addErrors: {},

      // Update stock modal
      showUpdateStockModal: false,
      updateSubmitLoading: false,
      updateModalItem: null,
      updateForm: {
        type: 'CONSUME',
        quantity: null,
        note: ''
      },
      updateErrors: {},

      // Threshold modal
      showThresholdModal: false,
      thresholdSubmitLoading: false,
      thresholdModalItem: null,
      thresholdForm: {
        low_stock_threshold: null
      },
      thresholdErrors: {},

      // Toasts
      toasts: []
    };
  },
  computed: {
    visibleItems() {
      const list = this.items.slice();
      const filtered = list.filter((it) => {
        const status = this.computedStatus(it);
        if (this.filterTab === 'OUT') return status === 'OUT';
        if (this.filterTab === 'LOW') return status === 'LOW';
        return true; // ALL
      });

      // OUT at top, then LOW, then NORMAL
      const rank = (s) => (s === 'OUT' ? 0 : s === 'LOW' ? 1 : 2);
      filtered.sort((a, b) => {
        const ra = rank(this.computedStatus(a));
        const rb = rank(this.computedStatus(b));
        if (ra !== rb) return ra - rb;
        return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
      });

      return filtered;
    },
    filteredLogs() {
      const list = this.logs.slice();
      const filtered = list.filter((l) => {
        if (this.logsFilterItemId === 'ALL') return true;
        return l.itemid === this.logsFilterItemId;
      });

      filtered.sort((a, b) => {
        // createdat is in `YYYY-MM-DD HH:mm:ss` format; Date parse should work.
        return new Date(b.createdat || 0).getTime() - new Date(a.createdat || 0).getTime();
      });

      return filtered;
    }
  },
  methods: {
    objectId() {
      // Kitchen APIs are scoped to objectId
      return this.$route?.params?.id;
    },

    showToast(message, type = 'error', duration = 4500) {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      this.toasts.push({ id, message, type });
      if (duration > 0) {
        window.setTimeout(() => this.removeToast(id), duration);
      }
    },
    removeToast(id) {
      this.toasts = this.toasts.filter((t) => t.id !== id);
    },

    parseNumber(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    },
    formatNumber(value) {
      const n = this.parseNumber(value);
      // Display up to 2 decimals (kitchen quantities are often fractional)
      return Number.isInteger(n) ? String(n) : String(n.toFixed(2)).replace(/\.?0+$/, '');
    },

    computedStatus(item) {
      const current = this.parseNumber(item.current_quantity);
      const low = this.parseNumber(item.low_stock_threshold);

      const apiStatus = String(item.stock_status || '').toUpperCase();
      if (apiStatus === 'OUT') return 'OUT';
      if (apiStatus === 'LOW') return current <= 0 ? 'OUT' : 'LOW';
      if (current <= 0) return 'OUT';
      if (current > 0 && current <= low) return 'LOW';
      return 'NORMAL';
    },
    isOut(item) {
      return this.computedStatus(item) === 'OUT';
    },
    itemCardClass(item) {
      return this.computedStatus(item) === 'OUT' ? 'card-out' : this.computedStatus(item) === 'LOW' ? 'card-low' : 'card-normal';
    },
    statusBadgeClass(item) {
      const s = this.computedStatus(item);
      return s === 'OUT' ? 'badge-out' : s === 'LOW' ? 'badge-low' : 'badge-normal';
    },
    isItemUpdating(itemId) {
      return !!this.itemUpdatingById[itemId];
    },
    canConsume(item, qty) {
      const current = this.parseNumber(item.current_quantity);
      return current >= qty && !this.isItemUpdating(item.itemid);
    },

    itemName(itemId) {
      return this.items.find((i) => i.itemid === itemId)?.name || 'Item';
    },
    itemUnit(itemId) {
      return this.items.find((i) => i.itemid === itemId)?.unit || '';
    },

    formatShortDate(createdAt) {
      if (!createdAt) return '';
      // Example: `2026-04-16 11:49:50`
      const d = new Date(String(createdAt).replace(' ', 'T'));
      if (Number.isNaN(d.getTime())) return String(createdAt);
      return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
    },

    async fetchKitchenItems() {
      const oid = this.objectId();
      if (!oid) return;

      this.itemsLoading = true;
      try {
        const response = await apiService.getKitchenItems(oid, 1, 15);
        const payload = response?.data?.data;
        this.items = Array.isArray(payload) ? payload : [];
      } catch (e) {
        console.error('Failed to fetch kitchen items:', e);
        // Keep UI empty but usable; provide a toast.
        this.showToast('Error occurred', 'error');
      } finally {
        this.itemsLoading = false;
      }
    },

    async fetchKitchenLogs() {
      const oid = this.objectId();
      if (!oid) return;

      this.logsLoading = true;
      try {
        const response = await apiService.getKitchenStockLogs(oid);
        const payload = response?.data?.data;
        this.logs = Array.isArray(payload) ? payload : [];
      } catch (e) {
        console.error('Failed to fetch kitchen stock logs:', e);
        this.showToast('Error occurred', 'error');
      } finally {
        this.logsLoading = false;
      }
    },

    openLogs() {
      this.viewMode = 'logs';
      this.logsFilterItemId = 'ALL';
      this.fetchKitchenLogs();
    },
    backToStock() {
      this.viewMode = 'dashboard';
    },

    openAddItemModal() {
      this.addErrors = {};
      this.addForm = { name: '', unit: 'kg', current_quantity: 0, low_stock_threshold: null };
      this.showAddItemModal = true;
      this.$nextTick(() => {
        this.$refs.addItemNameInput?.focus?.();
      });
    },
    closeAddItemModal() {
      this.showAddItemModal = false;
    },

    validateAddForm() {
      const errors = {};
      const name = String(this.addForm.name || '').trim();
      if (!name) errors.name = 'Item name is required';

      const allowedUnits = ['kg', 'litre', 'pcs'];
      if (!allowedUnits.includes(this.addForm.unit)) {
        errors.unit = 'Invalid unit';
      }

      const rawCurrent = this.addForm.current_quantity;
      const current = this.parseNumber(rawCurrent);
      if (rawCurrent === null || rawCurrent === '' || Number.isNaN(Number(rawCurrent))) {
        errors.current_quantity = 'Current quantity is required';
      } else if (!Number.isFinite(current) || current < 0) {
        errors.current_quantity = 'Current quantity must be 0 or greater';
      }

      const rawThreshold = this.addForm.low_stock_threshold;
      const threshold = this.parseNumber(rawThreshold);
      if (rawThreshold === null || rawThreshold === '' || Number.isNaN(Number(rawThreshold))) {
        errors.low_stock_threshold = 'Low Stock Threshold is required';
      } else if (!Number.isFinite(threshold) || threshold < 0) {
        errors.low_stock_threshold = 'Threshold must be 0 or greater';
      }

      return errors;
    },

    async submitAddItem() {
      this.addErrors = this.validateAddForm();
      if (Object.keys(this.addErrors).length > 0) return;

      const oid = this.objectId();
      if (!oid) return;

      this.addSubmitLoading = true;
      try {
        await apiService.addKitchenItem(oid, {
          name: this.addForm.name.trim(),
          unit: this.addForm.unit,
          current_quantity: this.parseNumber(this.addForm.current_quantity),
          low_stock_threshold: this.parseNumber(this.addForm.low_stock_threshold)
        });

        this.showToast('Item added', 'success');
        this.closeAddItemModal();
        await this.fetchKitchenItems();
      } catch (e) {
        console.error('Add kitchen item failed:', e);
        this.showToast('Error occurred', 'error');
      } finally {
        this.addSubmitLoading = false;
      }
    },

    openUpdateStockModal(item) {
      this.updateModalItem = item;
      this.updateErrors = {};
      this.updateForm = {
        type: 'CONSUME',
        quantity: null,
        note: ''
      };
      this.showUpdateStockModal = true;

      this.$nextTick(() => {
        this.$refs.updateQuantityInput?.focus?.();
      });
    },
    closeUpdateStockModal() {
      this.showUpdateStockModal = false;
      this.updateModalItem = null;
    },

    validateUpdateForm() {
      const errors = {};
      const qty = this.parseNumber(this.updateForm.quantity);
      if (!this.updateForm.type) errors.type = 'Type is required';
      if (!Number.isFinite(qty) || qty <= 0) errors.quantity = 'Quantity must be greater than 0';

      if (this.updateForm.type === 'CONSUME' && this.updateModalItem) {
        const current = this.parseNumber(this.updateModalItem.current_quantity);
        if (qty > current) errors.quantity = 'Consume quantity cannot exceed current stock';
      }

      return errors;
    },

    async submitUpdateStock() {
      if (!this.updateModalItem) return;

      this.updateErrors = this.validateUpdateForm();
      if (Object.keys(this.updateErrors).length > 0) return;

      const oid = this.objectId();
      if (!oid) return;

      const itemId = this.updateModalItem.itemid;
      const prevQty = this.parseNumber(this.updateModalItem.current_quantity);
      const qty = this.parseNumber(this.updateForm.quantity);
      const updateType = this.updateForm.type;

      // Optimistic update
      const optimisticQty = updateType === 'ADD' ? prevQty + qty : Math.max(0, prevQty - qty);
      this.prevQtyById[itemId] = prevQty;
      this.updateModalItem.current_quantity = optimisticQty;

      this.updateSubmitLoading = true;
      this.itemUpdatingById[itemId] = true;

      try {
        await apiService.updateKitchenStock(oid, {
          itemid: itemId,
          type: updateType,
          quantity: qty,
          note: this.updateForm.note || ''
        });

        this.showToast('Stock updated', 'success');
        this.closeUpdateStockModal();

        // Keep fast UX: refetch in background to reconcile any server rounding/stock-status changes.
        this.fetchKitchenItems();
      } catch (e) {
        console.error('Update stock failed:', e);
        this.updateModalItem.current_quantity = this.prevQtyById[itemId];
        this.showToast('Error occurred', 'error');
      } finally {
        this.updateSubmitLoading = false;
        this.itemUpdatingById[itemId] = false;
      }
    },

    quickUpdate(item, type, qty) {
      const oid = this.objectId();
      if (!oid) return;
      if (!item?.itemid) return;

      // Disable guards should prevent invalid clicks, but keep it safe.
      const current = this.parseNumber(item.current_quantity);
      const isConsume = type === 'CONSUME';
      if (isConsume && current < qty) return;
      if (this.isItemUpdating(item.itemid)) return;

      const itemId = item.itemid;
      const prevQty = current;
      const nextQty = isConsume ? Math.max(0, current - qty) : current + qty;

      // Optimistic UI
      this.prevQtyById[itemId] = prevQty;
      item.current_quantity = nextQty;
      this.itemUpdatingById[itemId] = true;

      apiService
        .updateKitchenStock(oid, {
          itemid: itemId,
          type,
          quantity: qty,
          note: isConsume ? `Consumed (${qty})` : `Added (${qty})`
        })
        .then(() => {
          this.showToast('Stock updated', 'success');
          // Reconcile stock-status/threshold states
          this.fetchKitchenItems();
        })
        .catch((e) => {
          console.error('Quick update failed:', e);
          item.current_quantity = this.prevQtyById[itemId];
          this.showToast('Error occurred', 'error');
        })
        .finally(() => {
          this.itemUpdatingById[itemId] = false;
        });
    },

    openThresholdModal(item) {
      this.thresholdModalItem = item;
      this.thresholdErrors = {};
      this.thresholdForm = {
        low_stock_threshold: this.parseNumber(item.low_stock_threshold)
      };
      this.showThresholdModal = true;
    },
    closeThresholdModal() {
      this.showThresholdModal = false;
      this.thresholdModalItem = null;
    },

    validateThresholdForm() {
      const errors = {};
      const v = this.parseNumber(this.thresholdForm.low_stock_threshold);
      if (!Number.isFinite(v) || v < 0) errors.low_stock_threshold = 'Threshold must be 0 or greater';
      return errors;
    },

    async submitThresholdUpdate() {
      if (!this.thresholdModalItem) return;
      this.thresholdErrors = this.validateThresholdForm();
      if (Object.keys(this.thresholdErrors).length > 0) return;

      const oid = this.objectId();
      if (!oid) return;

      const itemId = this.thresholdModalItem.itemid;
      this.thresholdSubmitLoading = true;
      this.itemUpdatingById[itemId] = true;

      try {
        await apiService.updateKitchenItemThreshold(
          itemId,
          oid,
          this.parseNumber(this.thresholdForm.low_stock_threshold)
        );
        this.showToast('Stock updated', 'success');
        this.closeThresholdModal();
        await this.fetchKitchenItems();
      } catch (e) {
        console.error('Threshold update failed:', e);
        this.showToast('Error occurred', 'error');
      } finally {
        this.thresholdSubmitLoading = false;
        this.itemUpdatingById[itemId] = false;
      }
    },

    askDeleteItem(item) {
      this.deleteModalItem = item;
      this.showDeleteConfirm = true;
    },
    closeDeleteConfirm() {
      this.showDeleteConfirm = false;
      this.deleteModalItem = null;
    },

    async confirmDelete() {
      if (!this.deleteModalItem) return;
      const oid = this.objectId();
      if (!oid) return;

      const itemId = this.deleteModalItem.itemid;
      this.deletingItemId = itemId;
      this.itemUpdatingById[itemId] = true;
      try {
        await apiService.deleteKitchenItem(itemId, oid);
        this.showToast('Stock updated', 'success');
        this.closeDeleteConfirm();
        await this.fetchKitchenItems();
      } catch (e) {
        console.error('Delete kitchen item failed:', e);
        this.showToast('Error occurred', 'error');
      } finally {
        this.deletingItemId = null;
        this.itemUpdatingById[itemId] = false;
      }
    }
  },
  mounted() {
    // Load dashboard list immediately for wardens.
    this.fetchKitchenItems();
  }
};
</script>

<style scoped>
.kitchen-page {
  width: 100%;
  padding: 12px 12px 18px;
}

.kitchen-top {
  background: linear-gradient(135deg, rgba(91, 46, 142, 0.06), rgba(124, 58, 237, 0.06));
  border: 1px solid rgba(91, 46, 142, 0.08);
  border-radius: 16px;
  padding: 12px;
  margin-bottom: 10px;
}

.kitchen-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.kitchen-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.02em;
}

.kitchen-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn {
  border: none;
  border-radius: 12px;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-icon {
  display: inline-block;
  margin-right: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(91, 46, 142, 0.18);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 30px rgba(91, 46, 142, 0.22);
}

.btn-secondary {
  background: rgba(91, 46, 142, 0.08);
  color: #5b2e8e;
  border: 1px solid rgba(91, 46, 142, 0.18);
}

.btn-ghost {
  background: rgba(91, 46, 142, 0.06);
  color: #5b2e8e;
  border: 1px solid rgba(91, 46, 142, 0.10);
  font-weight: 700;
}

.btn-danger-ghost {
  background: rgba(239, 68, 68, 0.06);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.15);
  font-weight: 700;
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.filter-tabs {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.tab {
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 900;
  border: 1px solid rgba(91, 46, 142, 0.14);
  background: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.15s ease;
}

.tab.active {
  background: #5b2e8e;
  color: #ffffff;
  border-color: rgba(91, 46, 142, 0.22);
  box-shadow: 0 10px 24px rgba(91, 46, 142, 0.18);
}

.kitchen-body {
  min-height: 50vh;
}

.logs-section {
  width: 100%;
  overflow-x: hidden;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.item-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(91, 46, 142, 0.10);
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.025);
}

.item-card.card-out {
  border-width: 2px;
  border-color: rgba(239, 68, 68, 0.28);
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.06), rgba(255, 255, 255, 0.92));
}

.item-card.card-out .item-quantity {
  color: #dc2626;
  font-weight: 1000;
}

.item-card.card-low {
  border-color: rgba(234, 179, 8, 0.28);
  background: linear-gradient(180deg, rgba(234, 179, 8, 0.08), rgba(255, 255, 255, 0.92));
}

.item-card.card-low .item-quantity {
  color: #b45309;
  font-weight: 950;
}

.item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.item-name-wrap {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.item-name {
  font-size: 15px;
  font-weight: 900;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-name-out {
  color: #dc2626;
  font-weight: 1000;
}

.out-label {
  font-size: 11px;
  font-weight: 1000;
  color: #ffffff;
  background: #dc2626;
  padding: 5px 9px;
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(220, 38, 38, 0.18);
  flex-shrink: 0;
}

.status-badge {
  font-size: 11px;
  font-weight: 1000;
  padding: 6px 9px;
  border-radius: 999px;
  border: 1px solid transparent;
}

.badge-normal {
  background: rgba(16, 185, 129, 0.10);
  color: #059669;
  border-color: rgba(16, 185, 129, 0.24);
}

.badge-low {
  background: rgba(234, 179, 8, 0.14);
  color: #b45309;
  border-color: rgba(234, 179, 8, 0.30);
}

.badge-out {
  background: rgba(239, 68, 68, 0.14);
  color: #dc2626;
  border-color: rgba(239, 68, 68, 0.34);
}

.item-quantity-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}

.item-quantity {
  font-size: 16px;
  font-weight: 950;
  color: #111827;
}

.unit {
  font-size: 13px;
  color: rgba(17, 24, 39, 0.7);
  font-weight: 900;
  margin-left: 6px;
}

.item-item-loader {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #5b2e8e;
  font-weight: 800;
  font-size: 12px;
}

.loader-text {
  font-weight: 900;
}

.tiny-spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(91, 46, 142, 0.20);
  border-top-color: rgba(91, 46, 142, 0.85);
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.quick-update-row {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.quick-group {
  flex: 1;
  border-radius: 14px;
  padding: 8px;
  border: 1px solid rgba(91, 46, 142, 0.12);
  background: rgba(255, 255, 255, 0.6);
}

.quick-group-consume {
  border-color: rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.05);
}

.quick-group-add {
  border-color: rgba(16, 185, 129, 0.20);
  background: rgba(16, 185, 129, 0.05);
}

.quick-group-title {
  font-size: 11px;
  font-weight: 1000;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 6px;
  color: rgba(17, 24, 39, 0.72);
}

.quick-group-consume .quick-group-title {
  color: rgba(220, 38, 38, 0.95);
}

.quick-group-add .quick-group-title {
  color: rgba(5, 150, 105, 0.98);
}

.quick-group-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.quick-btn {
  height: 36px;
  border-radius: 12px;
  font-weight: 1000;
  border: 1px solid rgba(91, 46, 142, 0.18);
  background: rgba(91, 46, 142, 0.06);
  color: #5b2e8e;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.quick-consume {
  border-color: rgba(220, 38, 38, 0.22);
  background: rgba(239, 68, 68, 0.07);
  color: #dc2626;
}

.quick-add {
  border-color: rgba(16, 185, 129, 0.25);
  background: rgba(16, 185, 129, 0.10);
  color: #059669;
}

.item-actions-row {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.empty-state {
  padding: 26px 14px;
  border: 1px dashed rgba(91, 46, 142, 0.20);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  text-align: center;
}

.empty-title {
  font-weight: 900;
  color: #111827;
  margin-bottom: 16px;
}

.logs-header {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
  padding-right: 16px;
}

.logs-title {
  margin: 0;
  font-size: 16px;
  font-weight: 1000;
  color: #111827;
  letter-spacing: -0.01em;
}

.logs-filters {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.filter-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.8);
}

.select {
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(91, 46, 142, 0.18);
  background: rgba(255, 255, 255, 0.92);
  font-weight: 800;
}

.logs-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: calc(100vh - 250px);
  overflow: auto;
  padding-right: 16px;
  padding-left: 2px;
  box-sizing: border-box;
}

.logs-columns {
  display: flex;
  gap: 12px;
  align-items: center;
  /* Align headings with log-row inner padding */
  padding: 0 28px 2px 14px;
  margin-top: 2px;
  margin-bottom: 6px;
  color: rgba(17, 24, 39, 0.55);
  font-weight: 1000;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  box-sizing: border-box;
}

.logs-col-change {
  min-width: 104px;
  text-align: left;
  /* Match `.log-amount-block` width so the details ("Item") aligns. */
  padding: 6px 10px;
  border-radius: 12px;
  border: 1px solid rgba(91, 46, 142, 0.10);
  background: rgba(91, 46, 142, 0.03);
}

.logs-col-details {
  flex: 1;
  min-width: 0;
}

.logs-details-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

.logs-heading-item,
.logs-heading-date {
  font-weight: 1000;
  font-size: 11px;
  letter-spacing: 0.02em;
}

.logs-heading-meta-line {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
}

.logs-heading-by {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.7);
  font-size: 11px;
  white-space: nowrap;
}

.logs-heading-by--placeholder {
  visibility: hidden;
}

.logs-heading-note {
  margin-top: 4px;
  font-weight: 1000;
  font-size: 11px;
  letter-spacing: 0.02em;
  width: 100%;
}

.log-row {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(91, 46, 142, 0.10);
  border-radius: 14px;
  padding: 12px;
  width: 100%;
  box-sizing: border-box;
}

.log-row--consume {
  border-color: rgba(239, 68, 68, 0.18);
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.05), rgba(255, 255, 255, 0.92));
}

.log-row--add {
  border-color: rgba(16, 185, 129, 0.18);
  background: linear-gradient(180deg, rgba(16, 185, 129, 0.05), rgba(255, 255, 255, 0.92));
}

.log-amount-block {
  min-width: 104px;
  padding: 6px 10px;
  border-radius: 12px;
  border: 1px solid rgba(91, 46, 142, 0.10);
  background: rgba(91, 46, 142, 0.03);
}

.log-row--consume .log-amount-block {
  border-color: rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.06);
}

.log-row--add .log-amount-block {
  border-color: rgba(16, 185, 129, 0.18);
  background: rgba(16, 185, 129, 0.07);
}

.log-amount-value {
  font-weight: 1000;
  color: #111827;
  font-size: 14px;
  line-height: 1.2;
}

.log-row--consume .log-amount-value {
  color: #dc2626;
}

.log-row--add .log-amount-value {
  color: #059669;
}

.log-sign {
  font-weight: 1000;
  margin-right: 2px;
}

.log-unit {
  font-weight: 900;
  font-size: 12px;
  color: rgba(17, 24, 39, 0.65);
  margin-left: 3px;
}

.log-type {
  margin-top: 3px;
  font-size: 11px;
  font-weight: 1000;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(17, 24, 39, 0.72);
}

.log-details {
  flex: 1;
  min-width: 0;
}

.log-item-name {
  font-weight: 1000;
  color: #111827;
  font-size: 14px;
  margin-bottom: 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}

.log-main-line {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.log-meta-line {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
  overflow: hidden;
}

.log-by {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.7);
  font-size: 12px;
  white-space: nowrap;
}

.log-dot {
  color: rgba(17, 24, 39, 0.35);
  font-weight: 900;
}

.log-date {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.7);
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.log-note {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 800;
  color: rgba(17, 24, 39, 0.85);
  width: 100%;
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  box-sizing: border-box;
}

.skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-card {
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(91, 46, 142, 0.10);
  background: rgba(255, 255, 255, 0.85);
}

.skeleton-card--log {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-title {
  height: 14px;
  width: 55%;
  background: rgba(91, 46, 142, 0.12);
  border-radius: 8px;
  animation: shimmer 1.1s infinite;
}

.skeleton-row {
  height: 12px;
  width: 90%;
  background: rgba(91, 46, 142, 0.10);
  border-radius: 8px;
  margin-top: 10px;
  animation: shimmer 1.1s infinite;
}

.skeleton-row--w {
  width: 60%;
}

@keyframes shimmer {
  0% {
    opacity: 0.65;
  }
  50% {
    opacity: 0.95;
  }
  100% {
    opacity: 0.65;
  }
}

/* Modal + Toast styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(10px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-content {
  width: 100%;
  max-width: 500px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 20px;
  border: 1px solid rgba(91, 46, 142, 0.12);
  box-shadow:
    0 25px 60px -25px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 120px);
  overflow: hidden;
}

.kitchen-modal {
  max-width: 560px;
}

.modal-header {
  padding: 14px 14px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 1000;
  color: #111827;
}

.close-btn {
  background: none;
  border: 2px solid rgba(91, 46, 142, 0.14);
  color: rgba(91, 46, 142, 0.9);
  border-radius: 12px;
  cursor: pointer;
  padding: 8px;
  transition: transform 0.15s ease, background 0.15s ease;
}

.close-btn:hover {
  transform: translateY(-1px);
  background: rgba(91, 46, 142, 0.06);
}

.modal-body {
  padding: 8px 14px 10px;
  overflow: auto;
}

.modal-footer {
  padding: 10px 14px 14px;
}

.kitchen-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.label {
  font-weight: 900;
  font-size: 13px;
  color: rgba(17, 24, 39, 0.8);
}

.input {
  border-radius: 14px;
  border: 1px solid rgba(91, 46, 142, 0.18);
  padding: 10px 12px;
  font-size: 15px;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.96);
  outline: none;
}

.input-large {
  font-size: 18px;
}

.textarea {
  font-weight: 700;
  resize: vertical;
}

.input:focus {
  border-color: rgba(91, 46, 142, 0.45);
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.12);
}

.input-error {
  border-color: rgba(239, 68, 68, 0.55) !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12) !important;
}

.field-error {
  color: #dc2626;
  font-weight: 900;
  font-size: 12px;
}

.radio-row {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.radio {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(91, 46, 142, 0.14);
  background: rgba(91, 46, 142, 0.05);
  font-weight: 900;
  color: rgba(91, 46, 142, 0.95);
}

.save-button {
  width: 100%;
  padding: 12px 14px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  font-size: 15px;
  font-weight: 1000;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(91, 46, 142, 0.20);
}

.save-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  box-shadow: none;
}

.toast-container {
  position: fixed;
  top: 78px;
  right: 16px;
  z-index: 11000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(320px, calc(100vw - 32px));
  pointer-events: none;
}

.toast {
  pointer-events: auto;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 20px 60px -30px rgba(0, 0, 0, 0.35);
  padding: 10px 10px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.toast-icon {
  color: rgba(91, 46, 142, 0.95);
  margin-top: 2px;
}

.toast-content {
  flex: 1;
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  font-size: 13px;
  line-height: 1.35;
}

.toast-close {
  background: none;
  border: none;
  color: rgba(17, 24, 39, 0.45);
  cursor: pointer;
  font-weight: 1000;
  padding: 0;
  margin-left: 6px;
}

.toast-success {
  border-left: 4px solid #10b981;
}

.toast-error {
  border-left: 4px solid #ef4444;
}

.toast-info {
  border-left: 4px solid #3b82f6;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(18px);
}

/* Delete Modal */
.delete-modal {
  max-width: 420px;
}

.delete-header {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.modal-icon {
  font-size: 20px;
}

.modal-description {
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  color: rgba(17, 24, 39, 0.75);
}

.modal-actions {
  padding: 14px 16px 16px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

@media (max-width: 480px) {
  .kitchen-page {
    padding: 12px 10px 16px;
  }
  .quick-update-row {
    gap: 8px;
  }
  .quick-group {
    padding: 7px;
  }
  .quick-btn {
    height: 34px;
    font-size: 13px;
  }
  .btn {
    padding: 8px 10px;
  }
  .modal-content {
    max-height: calc(100vh - 80px);
  }

  .logs-columns {
    display: none;
  }

  .log-row {
    padding: 10px;
    gap: 10px;
  }

  .log-amount-block {
    min-width: 92px;
  }

  .log-meta-line {
    flex-wrap: wrap;
    overflow: visible;
  }
}
</style>

